import React from "react";
import { createContext } from "react";
import PropTypes from "prop-types";
import { apiClient } from "../apiClient";
import { jwtDecode } from "jwt-decode";
import { useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  setAuthTokens,
  setUser,
  setUserDetails,
} from "../../services/reducers/authReducer";
import routesApi from "../apiRoutes";

const baseUrl = routesApi.base_url;
const AuthContext = createContext();

export default AuthContext;

const getErrorMessage = (error) => {
  if (!error.response) {
    return navigator.onLine
      ? "Unable to connect to server. Please check your connection."
      : "No internet connection detected.";
  }

  const { status, data } = error.response;

  switch (status) {
    case 401:
      return data.detail || "Invalid e-mail or password";
    case 429:
      return (
        data.detail ||
        "Too many login attempts. Please try again in 15 minutes."
      );
    default:
      return "An unexpected error occurred. Please contact support.";
  }
};

export const AuthProvider = ({ children }) => {
  const authTokens = useSelector((state) => state.auth.authTokens);
  const user = useSelector((state) => state.auth.user);
  const userDetails = useSelector((state) => state.auth.userDetails);
  const dispatch = useDispatch();
  const history = useHistory();

  const loginUser = async (e, setLoginError, setIsLoggedIn, redir) => {
    e.preventDefault();

    const credentials = {
      email: e.target.email.value,
      password: e.target.password.value,
    };

    try {
      const response = await apiClient.post(
        baseUrl + routesApi.token_obtain_pair,
        credentials,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (!response?.data) {
        setLoginError("Invalid response from server");
        return;
      }

      const { data } = response;

      dispatch(setAuthTokens(data));
      dispatch(setUser(jwtDecode(data.access)));
      setIsLoggedIn(true);

      if (redir && userDetails) {
        history.push(redir);
        return;
      }

      dispatch(setUserDetails(null));
      setTimeout(() => history.push("/welcome"), 600);
    } catch (error) {
      console.error("Login Error: ", error);
      setLoginError(getErrorMessage(error));
    }
  };

  let logoutUser = (e, error) => {
    if (e) e.preventDefault();
    dispatch(setAuthTokens(null));
    dispatch(setUser(null));
    dispatch(setUserDetails(null));
    history.push("/auth/login", error ? { error } : {});
  };

  let contextData = {
    user: user,
    userDetails: userDetails,
    authTokens: authTokens,
    loginUser: loginUser,
    logoutUser: logoutUser,
  };

  return (
    <AuthContext.Provider value={contextData}>{children}</AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.element.isRequired,
};
